import React from "react"
import {
  Alert,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  Grid,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material"

import { WHEEL_PRIZE_TYPE_LABELS, WHEEL_PRIZE_TYPES, WheelPrizeType, WinnerNotificationsFormData } from "./wheelPrizeTypes"

const ANONYMOUS_LABEL = "A WinFacil user"

const applyTemplate = (template: string, vars: Record<string, string>): string =>
  Object.entries(vars).reduce((text, [key, value]) => text.split(`{${key}}`).join(value), template)

export const WheelWinnerNotificationConfig: React.FC<{
  config: WinnerNotificationsFormData
  onChange: (patch: Partial<WinnerNotificationsFormData>) => void
}> = ({ config, onChange }) => {
  const previewName = config.privacyPolicy === "anonymous" ? ANONYMOUS_LABEL : "Maria R."
  const previewMessage = applyTemplate(config.template || "{name} just won {prize}!", {
    name: previewName,
    prize: "$25 Cash",
    time: new Date().toLocaleTimeString(),
  })

  return (
    <Stack spacing={3}>
      <Card variant="outlined">
        <CardContent>
          <Typography variant="subtitle1" sx={{ mb: 2 }}>
            Public winner announcements
          </Typography>
          <FormControlLabel
            control={<Switch checked={config.enabled} onChange={(event) => onChange({ enabled: event.target.checked })} />}
            label="Enable public winner announcements"
          />

          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                label="Announce"
                value={config.scope}
                disabled={!config.enabled}
                onChange={(event) => onChange({ scope: event.target.value as WinnerNotificationsFormData["scope"] })}
                fullWidth
              >
                <MenuItem value="all">All wins</MenuItem>
                <MenuItem value="grandPrizeOnly">Grand prizes only</MenuItem>
                <MenuItem value="selectedTypes">Selected prize types</MenuItem>
                <MenuItem value="disabled">Disabled</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                label="Winner display name"
                value={config.privacyPolicy}
                disabled={!config.enabled}
                onChange={(event) =>
                  onChange({ privacyPolicy: event.target.value as WinnerNotificationsFormData["privacyPolicy"] })
                }
                fullWidth
              >
                <MenuItem value="firstNameLastInitial">First name + last initial</MenuItem>
                <MenuItem value="anonymous">Anonymous ("A WinFacil user")</MenuItem>
              </TextField>
            </Grid>
          </Grid>

          {config.scope === "selectedTypes" && (
            <Select
              multiple
              fullWidth
              value={config.selectedTypes}
              onChange={(event) => onChange({ selectedTypes: event.target.value as WheelPrizeType[] })}
              renderValue={(selected) => selected.map((type) => WHEEL_PRIZE_TYPE_LABELS[type]).join(", ")}
              sx={{ mt: 2 }}
            >
              {WHEEL_PRIZE_TYPES.map((type) => (
                <MenuItem key={type} value={type}>
                  <Checkbox checked={config.selectedTypes.includes(type)} />
                  <ListItemText primary={WHEEL_PRIZE_TYPE_LABELS[type]} />
                </MenuItem>
              ))}
            </Select>
          )}

          <TextField
            label="Notification template"
            value={config.template}
            disabled={!config.enabled}
            onChange={(event) => onChange({ template: event.target.value })}
            helperText="Variables: {name} {prize} {time}"
            fullWidth
            sx={{ mt: 2 }}
          />
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardContent>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            Live preview
          </Typography>
          {config.enabled && config.scope !== "disabled" ? (
            <Alert severity="success" icon={false}>
              {previewMessage}
            </Alert>
          ) : (
            <Alert severity="info">Announcements are disabled — nothing will be shown to other users.</Alert>
          )}
        </CardContent>
      </Card>
    </Stack>
  )
}

export default WheelWinnerNotificationConfig
